import * as THREE from 'three';
import { emis, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * Dorn's guard automaton (Las vías 2): one of the Anvil's watchmen, bone and dressed stone like the crypt's Custodian
 * but small, a little shorter than a hero and twice as broad. A block of a body cut with a dwarven rune band, bleached
 * bone for limbs, a helm of stone with a slit lit red (it can't tell friend from foe), a stone maul for a right fist and
 * a bone blade on the left; on its back the iron hub Dorn winds it by. No brass, no gold.
 *
 * Las vías 4: Dorn's own (`friend`) come up the rails lit his cold blue-green, a friend's light. The `hulk` is the
 * biggest one Bruni turned: broader, plated with more stone, a dwarf's forge hammer for its right fist, cracks across its
 * chest and its slit lit Bruni's forge-blue.
 */

export type GuardPose = 'idle' | 'idleB' | 'walkA' | 'walkB' | 'windup' | 'strike' | 'off';
export const GUARD_POSES: readonly GuardPose[] = ['idle', 'idleB', 'walkA', 'walkB', 'windup', 'strike', 'off'];

export interface GuardModel extends Model {
  pose(p: GuardPose): void;
}

type Rot = [number, number, number];
interface Spec { body?: Rot; head?: Rot; armL?: Rot; armR?: Rot; legL?: Rot; legR?: Rot; y?: number }

/* Limbs hang along -Y: negative X swings them forward. */
const SPECS: Record<GuardPose, Spec> = {
  idle: { armR: [-0.15, 0, 0.12], armL: [-0.2, 0, -0.15] },
  idleB: { armR: [-0.1, 0, 0.14], armL: [-0.25, 0, -0.12], head: [0.05, 0.2, 0], y: -0.015 },
  walkA: { legL: [-0.42, 0, 0], legR: [0.36, 0, 0], armL: [0.3, 0, -0.15], armR: [-0.35, 0, 0.12], body: [0.1, 0.1, 0], y: 0.03 },
  walkB: { legL: [0.36, 0, 0], legR: [-0.42, 0, 0], armL: [-0.35, 0, -0.15], armR: [0.3, 0, 0.12], body: [0.1, -0.1, 0], y: 0.03 },
  // The maul cocked up behind the shoulder, the body wound round.
  windup: { armR: [-2.6, 0, 0.3], armL: [-0.6, 0, -0.5], body: [-0.12, 0.45, 0], head: [0, -0.25, 0], legL: [-0.3, 0, 0], legR: [0.25, 0, 0], y: -0.03 },
  // Brought down in front, the blade swept across.
  strike: { armR: [-0.9, 0, -0.15], armL: [-1.3, 0, 0.5], body: [0.35, -0.4, 0], head: [0.2, 0.25, 0], legL: [-0.4, 0, 0], legR: [0.35, 0, 0], y: -0.06 },
  // Wound down: slumped forward, arms hanging, head bowed, its light out.
  off: { armR: [0.15, 0, 0.05], armL: [0.15, 0, -0.05], body: [0.45, 0, 0.05], head: [0.55, 0, 0.1], legL: [-0.25, 0, -0.06], legR: [-0.25, 0, 0.06], y: -0.1 },
};

export interface GuardLook {
  /** Dorn's own (Las vías 4): his cold blue-green light. */
  friend?: boolean;
  /** The biggest automaton Bruni turned (Las vías 4): plated, a forge hammer, his blue. */
  hulk?: boolean;
}

export const guard = (o?: BaseOpts & GuardLook): GuardModel => {
  const c = begin(o, 0.62, 83);
  const hulk = !!o?.hulk;
  const light = hulk ? '#22c4ff' : o?.friend ? '#5fe8c8' : '#ff4f3a';
  const bone = std('#cfd0ca', { roughness: 0.85, flatShading: true });
  const oldBone = std('#8f918c', { roughness: 0.9, flatShading: true });
  const block = stone('#5a5e68');
  const dark = stone('#3a3d45');
  const rune = std('#b8c2d4', { roughness: 0.6, flatShading: true });
  const iron = metal('#3e4249', { roughness: 0.55, flatShading: true });
  const lit = hulk ? emis('#c8f6ff', '#22c4ff', 2.2) : o?.friend ? emis('#d8fff4', '#5fe8c8', 2.0) : emis('#ffd2c4', '#ff4f3a', 2.0);
  const unlit = std('#1a1a1e', { roughness: 0.9 });

  const fig = G(c.g);
  // Legs: short columns of bone, stone feet.
  const legs: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const hip = G(fig, k * 0.17, 0.5, 0);
    limb(hip, V(0, 0, 0), V(k * 0.02, -0.24, 0.03), 0.07, 0.06, bone, 6);
    M(new THREE.SphereGeometry(0.065, 6, 5), oldBone, hip).position.set(k * 0.02, -0.25, 0.03);
    limb(hip, V(k * 0.02, -0.25, 0.03), V(k * 0.01, -0.44, -0.01), 0.06, 0.05, oldBone, 6);
    const foot = M(new THREE.BoxGeometry(0.2, 0.08, 0.28), dark, hip);
    foot.position.set(k * 0.01, -0.46, 0.04);
    legs.push(hip);
  }
  // The body: a block of dressed stone, wider at the shoulders, a band of pale runes across the chest, iron straps.
  const body = G(fig, 0, 0.52, 0);
  const torso = M(new THREE.CylinderGeometry(0.34, 0.25, 0.52, 4, 1), block, body);
  torso.rotation.y = Math.PI / 4;
  torso.position.y = 0.3;
  torso.scale.set(1.1, 1, 0.8);
  M(new THREE.BoxGeometry(0.5, 0.12, 0.42), dark, body).position.set(0, 0.04, 0);
  for (const y of [0.18, 0.46]) M(new THREE.BoxGeometry(0.66, 0.04, 0.5), iron, body).position.set(0, y, 0);
  // The rune band: a few cut strokes, lit faintly from the stone.
  for (let i = 0; i < 5; i++) {
    const r = M(new THREE.BoxGeometry(0.025, 0.08 + (i % 2) * 0.04, 0.01), rune, body);
    r.position.set(-0.16 + i * 0.08, 0.33, 0.215);
    r.rotation.z = (i % 3 - 1) * 0.35;
  }
  // The ribs of bone at the sides, and the winding hub on its back (an iron boss with a square socket).
  for (const k of [-1, 1])
    for (let i = 0; i < 3; i++) {
      const rib = M(new THREE.TorusGeometry(0.1, 0.014, 3, 8, Math.PI), bone, body);
      rib.position.set(k * 0.3, 0.18 + i * 0.1, 0.02);
      rib.rotation.set(0, k * Math.PI / 2, 0);
    }
  const hub = M(new THREE.CylinderGeometry(0.1, 0.12, 0.08, 8), iron, body);
  hub.rotation.x = Math.PI / 2;
  hub.position.set(0, 0.32, -0.2);
  M(new THREE.BoxGeometry(0.05, 0.05, 0.03), unlit, body).position.set(0, 0.32, -0.25);
  // Shoulder stones.
  for (const k of [-1, 1]) {
    const sh = M(new THREE.DodecahedronGeometry(0.11, 0), block, body);
    sh.position.set(k * 0.34, 0.54, 0);
    sh.scale.set(1.1, 0.8, 1);
  }
  // The head: a stone helm sunk between the shoulders, a slit across it lit red; a crest of bone.
  const head = G(body, 0, 0.6, 0.03);
  const helm = M(new THREE.BoxGeometry(0.24, 0.2, 0.24), block, head);
  helm.position.y = 0.1;
  const crown = M(new THREE.CylinderGeometry(0.1, 0.13, 0.06, 4), dark, head);
  crown.position.y = 0.23;
  crown.rotation.y = Math.PI / 4;
  const crest = M(new THREE.ConeGeometry(0.03, 0.16, 4), bone, head);
  crest.position.set(0, 0.32, -0.02);
  M(new THREE.BoxGeometry(0.2, 0.045, 0.02), unlit, head).position.set(0, 0.11, 0.122);
  const eyes = M(new THREE.BoxGeometry(0.17, 0.028, 0.02), lit, head);
  eyes.position.set(0, 0.11, 0.128);
  const glow = glowSprite(head, light, 0.42, V(0, 0.11, 0.16), 0.75, true);
  // Arms: bone, a stone maul for the right fist, a bone blade on the left.
  const arms: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const arm = G(body, k * 0.38, 0.48, 0.02);
    limb(arm, V(0, 0, 0), V(k * 0.05, -0.26, 0.04), 0.055, 0.05, bone, 6);
    M(new THREE.SphereGeometry(0.055, 6, 5), oldBone, arm).position.set(k * 0.05, -0.27, 0.04);
    limb(arm, V(k * 0.05, -0.27, 0.04), V(k * 0.06, -0.48, 0.1), 0.05, 0.045, oldBone, 6);
    if (k > 0 && hulk) {
      // A dwarf's forge hammer: a long iron-shod haft, a heavy square head, one face worn bright.
      const h = G(arm, 0.07, -0.52, 0.12);
      M(new THREE.CylinderGeometry(0.025, 0.03, 0.62, 6), std('#4a372b', { roughness: 0.85 }), h).position.set(0, -0.12, 0.08);
      const hd = M(new THREE.BoxGeometry(0.34, 0.17, 0.17), iron, h);
      hd.position.set(0, -0.45, 0.08);
      M(new THREE.BoxGeometry(0.02, 0.15, 0.15), metal('#9aa0aa', { roughness: 0.35 }), h).position.set(0.18, -0.45, 0.08);
      M(new THREE.BoxGeometry(0.36, 0.03, 0.19), dark, h).position.set(0, -0.37, 0.08);
    } else if (k > 0) {
      const maul = M(new THREE.DodecahedronGeometry(0.13, 0), dark, arm);
      maul.position.set(0.07, -0.56, 0.12);
      maul.scale.set(1, 0.9, 1.15);
      M(new THREE.BoxGeometry(0.2, 0.04, 0.2), iron, arm).position.set(0.07, -0.5, 0.12);
    } else {
      const blade = G(arm, -0.06, -0.5, 0.12);
      const edge = M(new THREE.ConeGeometry(0.05, 0.4, 4), bone, blade);
      edge.position.set(0, -0.12, 0.1);
      edge.rotation.x = -Math.PI / 2 + 0.6;
      edge.scale.set(1, 1, 0.35);
      M(new THREE.BoxGeometry(0.09, 0.06, 0.09), iron, blade);
    }
    arms.push(arm);
  }
  if (hulk) {
    // More stone on it: plates over the shoulders and the belly, Bruni's blue showing through cracks across the chest.
    const crack = emis('#c8f6ff', '#22c4ff', 1.6);
    for (const k of [-1, 1]) {
      const pl = M(new THREE.BoxGeometry(0.26, 0.08, 0.3), dark, body);
      pl.position.set(k * 0.36, 0.64, 0);
      pl.rotation.z = k * -0.35;
    }
    M(new THREE.BoxGeometry(0.46, 0.14, 0.1), dark, body).position.set(0, 0.1, 0.21);
    for (let i = 0; i < 4; i++) {
      const cr = M(new THREE.BoxGeometry(0.015, 0.13 - i * 0.02, 0.012), crack, body);
      cr.position.set(-0.08 + i * 0.06, 0.24 + (i % 2) * 0.05, 0.222);
      cr.rotation.z = (i % 2 ? 0.6 : -0.5) + i * 0.1;
    }
  }
  const [armL, armR] = arms as [THREE.Group, THREE.Group];
  const [legL, legR] = legs as [THREE.Group, THREE.Group];
  const set = (g: THREE.Group, r: Rot | undefined): void => {
    g.rotation.set(r?.[0] ?? 0, r?.[1] ?? 0, r?.[2] ?? 0);
  };
  const glowMat = glow.material as THREE.SpriteMaterial;
  const pose = (p: GuardPose): void => {
    const s = SPECS[p];
    fig.position.y = s.y ?? 0;
    set(body, s.body);
    set(head, s.head);
    set(armL, s.armL);
    set(armR, s.armR);
    set(legL, s.legL);
    set(legR, s.legR);
    // Out: its light gone (the lit slit sinks into the helm, the glow fades).
    const off = p === 'off';
    eyes.position.z = off ? 0.09 : 0.128;
    glowMat.opacity = off ? 0 : 0.75;
    glow.visible = !off;
  };
  pose('idle');
  const m = done(c, 1.25, () => undefined);
  return { ...m, pose };
};

/**
 * Las vías 4: the biggest automaton Bruni turned, as a nightmare (`enemy:hulk`). Drawn by a nightmare's four tick
 * frames, which run on whatever it does: a heavy stomp, side to side.
 */
export const hulk = (o?: BaseOpts): GuardModel => {
  const m = guard({ ...o, hulk: true });
  const order: GuardPose[] = ['walkA', 'idleB', 'walkB', 'idle'];
  return { ...m, tick: (n: number) => m.pose(order[(n + 3) & 3] as GuardPose) };
};
